import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CTASection from '@/components/feature/CTASection';
import SEOHead from '@/components/base/SEOHead';

const SITE_URL = (typeof import.meta !== 'undefined' && (import.meta as Record<string, unknown>).env?.VITE_SITE_URL as string) || '';

const articles = [
  {
    id: 1,
    category: '政策資源',
    title: '經濟部產業競爭力輔導團：協助企業因應國際情勢與供應鏈變動',
    summary: '面對國際情勢變化、供應鏈重組與市場競爭壓力，企業需要更積極地導入數位工具、AI 應用、技術升級與營運優化。經濟部產業競爭力輔導團整合多項輔導能量，涵蓋 AI、數位轉型、人才培育、金融支持、技術輔導、市場拓展、節能與勞動力升級等方向，協助企業提升競爭力。',
    image: 'https://public.readdy.ai/ai/img_res/edited_d717c715bdca1510e072da3e1aa2245b_0e780f1d.jpg',
    datePublished: '2024-10-01',
  },
  {
    id: 2,
    category: '產業升級',
    title: '餐飲服務業優質成長與國際推廣：從雙語服務到流程升級',
    summary: '隨著國際旅客增加與服務品質要求提升，餐飲服務業不只需要提供好產品，也需要在菜單、接待流程、服務標示與顧客體驗上持續優化。透過中英雙語菜單、友善服務流程與示範場域建立，店家可逐步提升國際接待能力與品牌專業度。',
    image: 'https://public.readdy.ai/ai/img_res/edited_53a869f4ab48e09257231b03b941aaf9_0491f11a.jpg',
    datePublished: '2024-09-15',
  },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/news#webpage`,
  name: '最新資訊 | 得運智科股份有限公司',
  url: `${SITE_URL}/news`,
  description: '掌握政策趨勢、產業升級與企業轉型資源，協助企業在變動中找到下一步。得運智科最新資訊涵蓋政策資源、產業升級、企業轉型等主題。',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首頁', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '最新資訊', item: `${SITE_URL}/news` },
    ],
  },
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: articles.map((a, i) => ({
      '@type': 'Article',
      position: i + 1,
      headline: a.title,
      description: a.summary,
      datePublished: a.datePublished,
      url: `${SITE_URL}/news`,
      author: {
        '@type': 'Organization',
        name: '得運智科股份有限公司',
      },
      publisher: {
        '@type': 'Organization',
        name: '得運智科股份有限公司',
      },
      image: a.image,
      articleSection: a.category,
    })),
  },
};

export default function NewsPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title="最新資訊 | 得運智科股份有限公司"
        description="掌握政策趨勢、產業升級與企業轉型資源。涵蓋經濟部產業競爭力輔導團政策、餐飲服務業國際推廣等最新資訊，協助企業在變動中找到下一步。"
        keywords="企業轉型資訊, 政策資源, 產業升級, 企業新聞, 產業趨勢, 政府輔導"
        canonicalPath="/news"
        ogType="website"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main>
        {/* Hero with background image */}
        <section className="relative min-h-[400px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/edited_045e6d9cbeb6ac86888263379aba5f41_579bbc6f.jpg"
              alt="最新資訊背景"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-900/35 via-primary-900/20 to-primary-900/45" />
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-8 text-center pt-16">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              最新資訊
            </h1>
            <p className="text-sm md:text-base text-white/90 max-w-2xl mx-auto leading-relaxed">
              掌握政策趨勢、產業升級與企業轉型資源，協助企業在變動中找到下一步。
            </p>
          </div>
        </section>

        {/* Articles */}
        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {articles.map((article) => (
                <article
                  key={article.id}
                  className="bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 transition-colors duration-300"
                >
                  <div className="h-52 md:h-60 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <span className="inline-block px-3 py-1 bg-accent-100 text-accent-800 text-xs font-semibold rounded-full mb-3">
                      {article.category}
                    </span>
                    <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-3 leading-tight">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-6 line-clamp-3">
                      {article.summary}
                    </p>
                    <p className="text-xs text-foreground-500 mb-5 italic border-l-2 border-accent-300 pl-3">
                      實際申請資格、受理期間與審查結果，仍以主管機關最新公告為準。
                    </p>
                    <button className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors">
                      閱讀更多
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line" />
                      </div>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
}